import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

import { Trash2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { StepId } from "@/features/multistep/registry/types";
import { useEffect } from "react";
import { useLocations } from "@/hooks/use-location";

const Monedas = ["COP", "USD", "EUR"];

export function OperacionesInternacionalesStep({ stepId }: { stepId: StepId }) {
  const { control, trigger, setValue, resetField } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${stepId}.productosFinancieros`, // arreglo del detalle
  });

  const { paises } = useLocations();

  const realizaOperacionesInt = useWatch({
    control,
    name: `${stepId}.realizaOperaciones`,
  });

  useEffect(() => {
    trigger("operacionesInternacionales");
  }, [realizaOperacionesInt]);

  const otrasOperacion = useWatch({
    control,
    name: `${stepId}.operaciones.otras`,
  });

  const otrosProducto = useWatch({
    control,
    name: `${stepId}.productos.otros`,
  });

  const inversionProducto = useWatch({
    control,
    name: `${stepId}.productos.inversiones`,
  });

  const transferenciasProducto = useWatch({
    control,
    name: `${stepId}.productos.transferencias`,
  });

  useEffect(() => {
    if (realizaOperacionesInt === "no") {
      setValue(`${stepId}.operaciones`, {
        importaciones: undefined,
        exportaciones: undefined,
        otras: undefined,
        otrasDetalle: undefined,
      });
      setValue(`${stepId}.productos`, {
        inversiones: undefined,
        transferencias: undefined,
        otros: undefined,
        otrosDetalle: undefined,
      });
      setValue(`${stepId}.productosFinancieros`, []); // limpiar arreglo
    }
  }, [realizaOperacionesInt, setValue, stepId]);

  useEffect(() => {
    if (!otrasOperacion) {
      resetField(`${stepId}.operaciones.otrasDetalle`);
      setValue(`${stepId}.operaciones.otrasDetalle`, undefined);
    }
  }, [otrasOperacion, resetField, setValue, stepId]);

  useEffect(() => {
    if (!otrosProducto) {
      resetField(`${stepId}.productos.otrosDetalle`);
      setValue(`${stepId}.productos.otrosDetalle`, undefined);
    }
  }, [otrosProducto, resetField, setValue, stepId]);

  return (
    <div className="space-y-6">
      {/* ¿Realiza operaciones internacionales? */}
      <FormField
        control={control}
        name={`${stepId}.realizaOperaciones`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>¿Realiza operaciones internacionales?</FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="si" id="si" />
                  <label htmlFor="si">Sí</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="no" />
                  <label htmlFor="no">No</label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {realizaOperacionesInt === "si" && (
        <>
          {/* Tipos de operaciones */}
          <FormField
            control={control}
            name={`${stepId}.operaciones`}
            render={() => (
              <FormItem>
                <FormLabel>Indique cuáles</FormLabel>
                <div className="flex flex-wrap gap-4">
                  {["Importaciones", "Exportaciones", "Otras"].map((op) => (
                    <FormField
                      key={op}
                      control={control}
                      name={`${stepId}.operaciones.${op.toLowerCase()}`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="!mt-0">{op}</FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>

                {/* Campo detalle si marca "Otras" */}
                {otrasOperacion && (
                  <FormField
                    control={control}
                    name={`${stepId}.operaciones.otrasDetalle`}
                    render={({ field }) => (
                      <FormItem className="mt-2">
                        <FormLabel>Detalle otras operaciones</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Describa otras operaciones"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Productos financieros */}
          <FormField
            control={control}
            name={`${stepId}.productos`}
            render={() => (
              <FormItem>
                <FormLabel>Productos financieros en el exterior</FormLabel>
                <div className="flex flex-wrap gap-4">
                  {["Inversiones", "Transferencias", "Otros"].map((pf) => (
                    <FormField
                      key={pf}
                      name={`${stepId}.productos.${pf.toLowerCase()}`}
                      control={control}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="!mt-0">{pf}</FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>

                {/* Campo detalle si marca "Otros" */}
                {otrosProducto && (
                  <FormField
                    control={control}
                    name={`${stepId}.productos.otrosDetalle`}
                    render={({ field }) => (
                      <FormItem className="mt-2">
                        <FormLabel>Detalle otros productos</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Detalle otros productos"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Detalle de productos */}
          {(otrosProducto || inversionProducto || transferenciasProducto) && (
            <FormField
              control={control}
              name={`${stepId}.productosFinancieros`}
              render={() => (
                <FormItem className="mt-2">
                  <div>
                    <FormLabel className="mb-4">
                      Detalle de productos financieros en el exterior
                    </FormLabel>
                    <div className="space-y-4">
                      {fields.map((item, index) => (
                        <div key={item.id} className="space-y-2">
                          <div className="flex flex-col md:flex-row gap-4 items-start">
                            <FormField
                              control={control}
                              name={`${stepId}.productosFinancieros.${index}.tipo`}
                              render={({ field }) => (
                                <FormItem className="flex-1 min-w-0">
                                  <FormControl>
                                    <Input
                                      {...field}
                                      placeholder="Tipo"
                                    />
                                  </FormControl>
                                  <FormMessage className="md:hidden" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name={`${stepId}.productosFinancieros.${index}.numero`}
                              render={({ field }) => (
                                <FormItem className="flex-1 min-w-0">
                                  <FormControl>
                                    <Input {...field} placeholder="Número" />
                                  </FormControl>
                                  <FormMessage className="md:hidden" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name={`${stepId}.productosFinancieros.${index}.entidad`}
                              render={({ field }) => (
                                <FormItem className="flex-1 min-w-0">
                                  <FormControl>
                                    <Input {...field} placeholder="Entidad" />
                                  </FormControl>
                                  <FormMessage className="md:hidden" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name={`${stepId}.productosFinancieros.${index}.monto`}
                              render={({ field }) => (
                                <FormItem className="w-full md:w-24 min-w-0">
                                  <FormControl>
                                    <div className="flex items-center">
                                      <span className="mr-1">$</span>
                                      <Input type="number" {...field} />
                                    </div>
                                  </FormControl>
                                  <FormMessage className="md:hidden" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name={`${stepId}.productosFinancieros.${index}.pais`}
                              render={({ field }) => (
                                <FormItem className="flex-1 min-w-0">
                                  <FormControl>
                                    <Select
                                      onValueChange={(label) => {
                                        // Si quieres que el form guarde el id real
                                        field.onChange(label ?? "");
                                      }}
                                      value={field.value ?? ""}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="País" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {paises.map((p) => (
                                          <SelectItem
                                            key={p.value}
                                            value={p.label}
                                          >
                                            {p.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage className="md:hidden" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={control}
                              name={`${stepId}.productosFinancieros.${index}.moneda`}
                              render={({ field }) => (
                                <FormItem className="w-full md:w-24 min-w-0">
                                  <FormControl>
                                    <Select
                                      onValueChange={field.onChange}
                                      value={field.value}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Moneda" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {Monedas.map((m) => (
                                          <SelectItem key={m} value={m}>
                                            {m}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage className="md:hidden" />
                                </FormItem>
                              )}
                            />

                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              onClick={() => remove(index)}
                              className="h-10 w-10 shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Mensajes de error para desktop */}
                          <div className="hidden md:flex md:flex-row gap-4">
                            <div className="flex-1 min-w-0">
                              <FormField
                                control={control}
                                name={`${stepId}.productosFinancieros.${index}.tipo`}
                                render={() => <FormMessage />}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <FormField
                                control={control}
                                name={`${stepId}.productosFinancieros.${index}.numero`}
                                render={() => <FormMessage />}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <FormField
                                control={control}
                                name={`${stepId}.productosFinancieros.${index}.entidad`}
                                render={() => <FormMessage />}
                              />
                            </div>
                            <div className="w-24 min-w-0">
                              <FormField
                                control={control}
                                name={`${stepId}.productosFinancieros.${index}.monto`}
                                render={() => <FormMessage />}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <FormField
                                control={control}
                                name={`${stepId}.productosFinancieros.${index}.pais`}
                                render={() => <FormMessage />}
                              />
                            </div>
                            <div className="w-24 min-w-0">
                              <FormField
                                control={control}
                                name={`${stepId}.productosFinancieros.${index}.moneda`}
                                render={() => <FormMessage />}
                              />
                            </div>
                            <div className="w-10 shrink-0"></div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <FormMessage />
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-4"
                      onClick={() =>
                        append({
                          tipo: "",
                          numero: "",
                          entidad: "",
                          monto: 0,
                          pais: "",
                          moneda: "",
                        })
                      }
                    >
                      Agregar producto
                    </Button>
                  </div>
                </FormItem>
              )}
            />
          )}
        </>
      )}
    </div>
  );
}
